import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, dateFR } from "@/lib/api";
import { AlertTriangle, Wallet, Clock, CalendarDays, Plane, PackageCheck, X, ChevronDown, ChevronUp, RotateCcw, BookMarked } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { fr } from "date-fns/locale";
import StatusBadge from "@/components/StatusBadge";
import Plate from "@/components/Plate";
import { QuickVenteDialog, QuickPaiementDialog } from "@/components/QuickActions";
import VenteFormDialog from "@/components/VenteFormDialog";
import QuickSearchBar from "@/components/QuickSearchBar";
import { useAuth } from "@/lib/auth";

function isoDay(d) {
  if (!d) return "";
  if (typeof d === "string") {
    return d.length >= 10 ? d.slice(0, 10) : d;
  }
  // Use LOCAL date components to avoid UTC timezone shift
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const isCommercial = user?.role === "commercial";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ventes, setVentes] = useState([]);
  const [selectedDay, setSelectedDay] = useState(new Date());

  const reload = async () => {
    setLoading(true);
    const [d, v] = await Promise.all([api.get("/dashboard"), api.get("/ventes")]);
    setData(d.data);
    setVentes(v.data);
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const [expanded, setExpanded] = useState({}); // { retard_paiement: true, ... }
  const [showSnoozedDialog, setShowSnoozedDialog] = useState(false);
  // Sort for retard_paiement alerts: date-desc | date-asc | montant-desc | montant-asc | jours-desc
  const [retardSort, setRetardSort] = useState("date-desc");

  const snoozeAlert = async (type, vente_id) => {
    try {
      await api.post(`/dashboard/alerts/${type}/${vente_id}/snooze`);
      reload();
    } catch { /* ignore */ }
  };

  const unsnoozeAlert = async (type, vente_id) => {
    try {
      await api.delete(`/dashboard/alerts/${type}/${vente_id}/snooze`);
      reload();
    } catch { /* ignore */ }
  };

  const snoozedAlerts = data?.snoozed_alerts || [];

  // Sort retard_paiement alerts client-side based on user choice
  const sortedRetard = useMemo(() => {
    const list = [...(data?.alertes_retard_paiement || [])];
    switch (retardSort) {
      case "date-asc":
        list.sort((a, b) => (a.date_vente || "").localeCompare(b.date_vente || ""));
        break;
      case "montant-desc":
        list.sort((a, b) => Number(b.montant_restant || 0) - Number(a.montant_restant || 0));
        break;
      case "montant-asc":
        list.sort((a, b) => Number(a.montant_restant || 0) - Number(b.montant_restant || 0));
        break;
      case "jours-desc":
        list.sort((a, b) => Number(b.jours_retard || 0) - Number(a.jours_retard || 0));
        break;
      case "date-desc":
      default:
        list.sort((a, b) => (b.date_vente || "").localeCompare(a.date_vente || ""));
    }
    return list;
  }, [data?.alertes_retard_paiement, retardSort]);

  // Build calendar markers
  const { saleDays, departDays, dayMap } = useMemo(() => {
    const sd = new Set();
    const dd = new Set();
    const map = {}; // ISO day -> { ventes:[], departs:[] }
    for (const v of ventes) {
      if (v.date_vente) {
        const key = isoDay(v.date_vente);
        sd.add(key);
        if (!map[key]) map[key] = { ventes: [], departs: [] };
        map[key].ventes.push(v);
      }
      if (v.date_depart) {
        const key = isoDay(v.date_depart);
        dd.add(key);
        if (!map[key]) map[key] = { ventes: [], departs: [] };
        map[key].departs.push(v);
      }
    }
    return { saleDays: sd, departDays: dd, dayMap: map };
  }, [ventes]);

  const selectedKey = isoDay(selectedDay);
  const dayInfo = dayMap[selectedKey] || { ventes: [], departs: [] };

  if (loading || !data) {
    return <div className="text-sm text-muted-foreground">Chargement du tableau de bord…</div>;
  }

  const noAlerts =
    data.nb_alertes_carte_grise === 0 &&
    data.nb_alertes_retard_paiement === 0 &&
    data.nb_alertes_depart_proche === 0 &&
    data.nb_alertes_a_preparer === 0 &&
    data.nb_acomptes_libres === 0;

  // Build day modifiers for react-day-picker
  const modifiers = {
    saleDay: Array.from(saleDays).map((s) => {
      const [y, m, d] = s.split("-").map(Number);
      return new Date(y, m - 1, d);
    }),
    departDay: Array.from(departDays).map((s) => {
      const [y, m, d] = s.split("-").map(Number);
      return new Date(y, m - 1, d);
    }),
  };
  const modifiersStyles = {
    saleDay: { fontWeight: 700, color: "#002FA7" },
    departDay: { textDecoration: "underline", textDecorationColor: "#E63946", textDecorationThickness: "2px", textUnderlineOffset: "3px" },
  };

  return (
    <div className="space-y-8">
      {/* Live quick-search */}
      <div className="max-w-2xl">
        <QuickSearchBar testId="dashboard-search" autoFocus={false} />
      </div>

      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
            Tableau de bord
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-1">
            Centre d'alertes & activité
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <QuickPaiementDialog onCreated={reload} />
          <VenteFormDialog onCreated={reload} triggerTestId="dashboard-vente-add-btn" />
        </div>
      </header>

      {/* Tâches internes en attente */}
      {(user?.role === "commercial" || user?.role === "admin") && <PendingTasksCard />}

      {/* Alertes */}
      <div className="space-y-6">
        {snoozedAlerts.length > 0 && (
          <div className="flex items-center justify-end -mb-2" data-testid="snoozed-alerts-bar">
            <button
              onClick={() => setShowSnoozedDialog(true)}
              data-testid="snoozed-alerts-open"
              className="text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border hover:bg-slate-50"
            >
              <RotateCcw size={12} /> Alertes masquées · {snoozedAlerts.length}
            </button>
          </div>
        )}
        {noAlerts && (
          <div className="bg-card border border-border rounded-sm p-10 text-center" data-testid="no-alerts">
            <div className="inline-flex p-3 rounded-sm bg-emerald-50 text-emerald-700 mb-3">
              <Clock size={20} />
            </div>
            <h3 className="font-display font-bold text-xl">Aucune alerte</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Toutes les ventes sont à jour. Bravo !
            </p>
          </div>
        )}

        {data.nb_alertes_a_preparer > 0 && (
          <div className="border-l-4 border-orange-500 bg-orange-50/60 rounded-sm p-5" data-testid="alert-preparer">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-orange-100 text-orange-700 rounded-sm shrink-0">
                <PackageCheck size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] tracking-[0.18em] uppercase text-orange-700 font-bold">
                  Priorité atelier · À préparer pour livraison (≤ 3 jours)
                </div>
                <h4 className="font-display font-bold text-lg mt-0.5">
                  {data.nb_alertes_a_preparer} véhicule(s) à préparer maintenant
                </h4>
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="pb-2">Plaque</th>
                        <th className="pb-2">Véhicule</th>
                        <th className="pb-2">Client</th>
                        <th className="pb-2">Livraison</th>
                        <th className="pb-2 text-center">Carte grise</th>
                        <th className="pb-2 text-center">Statut</th>
                        <th className="pb-2 text-right">Échéance</th>
                        <th className="pb-2 text-right w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-orange-200/60">
                      {(expanded.a_preparer ? data.alertes_a_preparer : data.alertes_a_preparer.slice(0, 5)).map((v) => (
                        <tr key={v.id}>
                          <td className="py-2"><Link to={`/ventes/${v.id}`}><Plate value={v.plaque} small /></Link></td>
                          <td className="py-2"><Link to={`/ventes/${v.id}`} className="hover:underline">{v.marque} {v.modele}</Link></td>
                          <td className="py-2">{v.client_nom}</td>
                          <td className="py-2 text-muted-foreground">{dateFR(v.date_depart)}</td>
                          <td className="py-2 text-center">
                            {v.carte_grise_disponible
                              ? <span className="text-emerald-700 text-xs font-semibold">OK</span>
                              : <span className="text-destructive text-xs font-semibold">Manquante</span>}
                          </td>
                          <td className="py-2 text-center">
                            {v.preparer_a_livrer
                              ? <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">Prêt à livrer</span>
                              : <span className="text-muted-foreground text-xs">À préparer</span>}
                          </td>
                          <td className="py-2 text-right">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-bold ${
                              v.jours_avant_depart === 0 ? "bg-red-100 text-destructive"
                              : v.jours_avant_depart === 1 ? "bg-orange-200 text-orange-900"
                              : "bg-orange-100 text-orange-800"
                            }`}>
                              {v.jours_avant_depart === 0 ? "Aujourd'hui" : `J-${v.jours_avant_depart}`}
                            </span>
                          </td>
                          <td className="py-2 text-right">
                            <button
                              onClick={() => snoozeAlert("a_preparer", v.id)}
                              data-testid={`snooze-prep-${v.id}`}
                              title="Masquer cette alerte"
                              className="text-muted-foreground hover:text-destructive p-1 rounded-sm hover:bg-orange-100/50"
                            >
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {data.alertes_a_preparer.length > 5 && (
                    <button
                      onClick={() => setExpanded((e) => ({ ...e, a_preparer: !e.a_preparer }))}
                      className="mt-3 text-xs text-orange-700 font-semibold uppercase tracking-wider inline-flex items-center gap-1 hover:underline"
                    >
                      {expanded.a_preparer ? <>Réduire <ChevronUp size={12} /></> : <>Afficher les {data.alertes_a_preparer.length - 5} restant(s) <ChevronDown size={12} /></>}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {data.nb_alertes_depart_proche > 0 && (
          <div className="border-l-4 border-amber-500 bg-amber-50/60 rounded-sm p-5" data-testid="alert-depart">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-amber-100 text-amber-700 rounded-sm shrink-0">
                <Plane size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] tracking-[0.18em] uppercase text-amber-700 font-bold">
                  À venir · Départs véhicule (≤ 7 jours)
                </div>
                <h4 className="font-display font-bold text-lg mt-0.5">
                  {data.nb_alertes_depart_proche} départ(s) prévu(s) dans la semaine
                </h4>
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="pb-2">Plaque</th>
                        <th className="pb-2">Véhicule</th>
                        <th className="pb-2">Client</th>
                        <th className="pb-2">Date départ</th>
                        <th className="pb-2 text-center">Carte grise</th>
                        <th className="pb-2 text-right">Restant</th>
                        <th className="pb-2 text-right">Dans</th>
                        <th className="pb-2 text-right w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-200/50">
                      {(expanded.depart_proche ? data.alertes_depart_proche : data.alertes_depart_proche.slice(0, 5)).map((v) => (
                        <tr key={v.id}>
                          <td className="py-2"><Link to={`/ventes/${v.id}`}><Plate value={v.plaque} small /></Link></td>
                          <td className="py-2"><Link to={`/ventes/${v.id}`} className="hover:underline">{v.marque} {v.modele}</Link></td>
                          <td className="py-2">{v.client_nom}</td>
                          <td className="py-2 text-muted-foreground">{dateFR(v.date_depart)}</td>
                          <td className="py-2 text-center">
                            {v.carte_grise_disponible
                              ? <span className="text-emerald-700 text-xs font-semibold">OK</span>
                              : <span className="text-destructive text-xs font-semibold">Manquante</span>}
                          </td>
                          <td className="py-2 text-right font-mono">{money(v.montant_restant)}</td>
                          <td className="py-2 text-right">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-bold ${
                              v.jours_avant_depart === 0 ? "bg-red-100 text-destructive"
                              : v.jours_avant_depart <= 2 ? "bg-amber-200 text-amber-900"
                              : "bg-amber-100 text-amber-800"
                            }`}>
                              {v.jours_avant_depart === 0 ? "Aujourd'hui" : `J-${v.jours_avant_depart}`}
                            </span>
                          </td>
                          <td className="py-2 text-right">
                            <button onClick={() => snoozeAlert("depart_proche", v.id)} data-testid={`snooze-depart-${v.id}`} title="Masquer" className="text-muted-foreground hover:text-destructive p-1 rounded-sm hover:bg-amber-100/50"><X size={14} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {data.alertes_depart_proche.length > 5 && (
                    <button onClick={() => setExpanded((e) => ({ ...e, depart_proche: !e.depart_proche }))} className="mt-3 text-xs text-amber-800 font-semibold uppercase tracking-wider inline-flex items-center gap-1 hover:underline">
                      {expanded.depart_proche ? <>Réduire <ChevronUp size={12} /></> : <>Afficher les {data.alertes_depart_proche.length - 5} restant(s) <ChevronDown size={12} /></>}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {data.nb_alertes_retard_paiement > 0 && (
          <div className="border-l-4 border-destructive bg-red-50/60 rounded-sm p-5" data-testid="alert-retard">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-red-100 text-destructive rounded-sm shrink-0">
                <Clock size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] tracking-[0.18em] uppercase text-destructive font-bold">
                  Alerte · Retard paiement
                </div>
                <h4 className="font-display font-bold text-lg mt-0.5">
                  {data.nb_alertes_retard_paiement} VO vendu(s) non soldé(s) — plus de 10 jours
                </h4>
                <div className="mt-2 mb-3 flex items-center gap-2 text-xs">
                  <span className="uppercase tracking-wider text-muted-foreground font-semibold">Trier par</span>
                  <select
                    data-testid="retard-sort"
                    value={retardSort}
                    onChange={(e) => setRetardSort(e.target.value)}
                    className="h-8 rounded-sm border border-input bg-background px-2 text-xs"
                  >
                    <option value="date-desc">Date vente · plus récent</option>
                    <option value="date-asc">Date vente · plus ancien</option>
                    <option value="montant-desc">Restant dû · décroissant</option>
                    <option value="montant-asc">Restant dû · croissant</option>
                    <option value="jours-desc">Jours de retard · décroissant</option>
                  </select>
                </div>
                <div className="mt-1 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="pb-2">Plaque</th>
                        <th className="pb-2">Véhicule</th>
                        <th className="pb-2">Client</th>
                        <th className="pb-2">Date vente</th>
                        <th className="pb-2 text-right">Restant dû</th>
                        <th className="pb-2 text-right">Retard</th>
                        <th className="pb-2 text-right w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-200/50">
                      {(expanded.retard_paiement
                        ? sortedRetard
                        : sortedRetard.slice(0, 5)
                      ).map((v) => (
                        <tr key={v.id}>
                          <td className="py-2"><Link to={`/ventes/${v.id}`}><Plate value={v.plaque} small /></Link></td>
                          <td className="py-2"><Link to={`/ventes/${v.id}`} className="hover:underline">{v.marque} {v.modele}</Link></td>
                          <td className="py-2">{v.client_nom}</td>
                          <td className="py-2 text-muted-foreground">{dateFR(v.date_vente)}</td>
                          <td className="py-2 text-right font-mono font-semibold text-destructive">{money(v.montant_restant)}</td>
                          <td className="py-2 text-right">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-bold bg-red-100 text-destructive">
                              {v.jours_retard} j
                            </span>
                          </td>
                          <td className="py-2 text-right">
                            <button
                              onClick={() => snoozeAlert("retard_paiement", v.id)}
                              data-testid={`snooze-retard-${v.id}`}
                              title="Masquer cette alerte"
                              className="text-muted-foreground hover:text-destructive p-1 rounded-sm hover:bg-red-100/50"
                            >
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {sortedRetard.length > 5 && (
                    <button
                      onClick={() => setExpanded((e) => ({ ...e, retard_paiement: !e.retard_paiement }))}
                      data-testid="alert-retard-toggle"
                      className="mt-3 text-xs text-destructive font-semibold uppercase tracking-wider inline-flex items-center gap-1 hover:underline"
                    >
                      {expanded.retard_paiement ? (
                        <>Réduire <ChevronUp size={12} /></>
                      ) : (
                        <>Afficher les {sortedRetard.length - 5} restant(s) <ChevronDown size={12} /></>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {data.nb_alertes_carte_grise > 0 && (
          <div className="border-l-4 border-destructive bg-red-50/60 rounded-sm p-5 flex items-start gap-4" data-testid="alert-cartegrise">
            <div className="p-2 bg-red-100 text-destructive rounded-sm shrink-0">
              <AlertTriangle size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] tracking-[0.18em] uppercase text-destructive font-bold">
                Alerte · Carte grise NON REÇUE
              </div>
              <h4 className="font-display font-bold text-lg mt-0.5">
                {data.nb_alertes_carte_grise} véhicule(s) sans carte grise disponible
              </h4>
              <ul className="mt-3 space-y-1.5 max-h-60 overflow-y-auto text-sm">
                {(expanded.carte_grise ? data.alertes_carte_grise : data.alertes_carte_grise.slice(0, 5)).map((v) => (
                  <li key={v.id} className="flex items-center justify-between gap-3 group">
                    <Link to={`/ventes/${v.id}`} className="flex items-center gap-2 min-w-0 hover:underline">
                      <Plate value={v.plaque} small />
                      <span className="truncate">{v.marque} {v.modele} — {v.client_nom}</span>
                    </Link>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {v.date_depart ? `Départ ${dateFR(v.date_depart)}` : dateFR(v.date_vente)}
                      </span>
                      <button onClick={() => snoozeAlert("carte_grise", v.id)} data-testid={`snooze-cg-${v.id}`} title="Masquer" className="text-muted-foreground hover:text-destructive p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"><X size={14} /></button>
                    </div>
                  </li>
                ))}
              </ul>
              {data.alertes_carte_grise.length > 5 && (
                <button onClick={() => setExpanded((e) => ({ ...e, carte_grise: !e.carte_grise }))} className="mt-2 text-xs text-destructive font-semibold uppercase tracking-wider inline-flex items-center gap-1 hover:underline">
                  {expanded.carte_grise ? <>Réduire <ChevronUp size={12} /></> : <>Afficher les {data.alertes_carte_grise.length - 5} restant(s) <ChevronDown size={12} /></>}
                </button>
              )}
            </div>
          </div>
        )}

        {data.nb_acomptes_libres > 0 && (
          <div className="border-l-4 border-amber-400 bg-amber-50/60 rounded-sm p-5 flex items-start gap-4" data-testid="alert-acomptes">
            <div className="p-2 bg-amber-100 text-amber-700 rounded-sm shrink-0">
              <Wallet size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] tracking-[0.18em] uppercase text-amber-700 font-bold">
                À traiter · Acomptes libres
              </div>
              <h4 className="font-display font-bold text-lg mt-0.5">
                {data.nb_acomptes_libres} acompte(s) — {money(data.total_acomptes_libres)}
              </h4>
              <p className="text-sm text-amber-800 mt-2">
                Des paiements ne sont pas encore affectés à une vente.{" "}
                <Link to="/paiements" className="underline font-semibold">Affecter maintenant →</Link>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Calendar + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-sm p-5" data-testid="dashboard-calendar">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays size={16} className="text-klein" />
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground font-semibold">Planning</div>
              <h3 className="font-display font-bold text-lg">Calendrier</h3>
            </div>
          </div>
          <Calendar
            mode="single"
            selected={selectedDay}
            onSelect={(d) => d && setSelectedDay(d)}
            locale={fr}
            modifiers={modifiers}
            modifiersStyles={modifiersStyles}
            className="rounded-sm border-0 p-0"
          />
          <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><span className="font-bold text-klein">●</span> Vente</span>
            <span className="inline-flex items-center gap-1.5"><span className="border-b-2 border-destructive">__</span> Départ</span>
          </div>
        </div>

        <div className="lg:col-span-2 bg-card border border-border rounded-sm p-6">
          <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground font-semibold">
            Jour sélectionné · {selectedDay.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
          </div>
          <h3 className="font-display font-bold text-xl mt-0.5">
            Activité du jour
          </h3>

          {dayInfo.ventes.length === 0 && dayInfo.departs.length === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground">Aucune activité ce jour.</div>
          )}

          {dayInfo.ventes.length > 0 && (
            <div className="mt-5">
              <div className="text-[11px] tracking-[0.18em] uppercase text-klein font-bold mb-2">
                Ventes · {dayInfo.ventes.length}
              </div>
              <ul className="divide-y border border-border rounded-sm">
                {dayInfo.ventes.map((v) => (
                  <li key={`v-${v.id}`}>
                    <Link to={`/ventes/${v.id}`} className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-slate-50">
                      <div className="flex items-center gap-3 min-w-0">
                        <Plate value={v.plaque} small />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{v.marque} {v.modele} — {v.client_nom}</div>
                          <div className="text-xs text-muted-foreground">{money(v.prix_vente)}</div>
                        </div>
                      </div>
                      <StatusBadge statut={v.statut} />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {dayInfo.departs.length > 0 && (
            <div className="mt-5">
              <div className="text-[11px] tracking-[0.18em] uppercase text-destructive font-bold mb-2">
                Départs véhicule · {dayInfo.departs.length}
              </div>
              <ul className="divide-y border border-border rounded-sm">
                {dayInfo.departs.map((v) => (
                  <li key={`d-${v.id}`}>
                    <Link to={`/ventes/${v.id}`} className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-slate-50">
                      <div className="flex items-center gap-3 min-w-0">
                        <Plate value={v.plaque} small />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{v.marque} {v.modele} — {v.client_nom}</div>
                          <div className="text-xs text-muted-foreground">
                            {v.carte_grise_disponible ? "Carte grise OK" : <span className="text-destructive">Carte grise manquante</span>}
                          </div>
                        </div>
                      </div>
                      <StatusBadge statut={v.statut} />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Ventes en cours */}
      <div className="bg-card border border-border rounded-sm">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground font-semibold">
              Activité en cours
            </div>
            <h3 className="font-display font-bold text-xl">Ventes en cours · {data.nb_voitures_impayees}</h3>
            {!isCommercial && (
              <p className="text-sm mt-1">
                Créance totale : <span className="font-display font-bold text-destructive" data-testid="ventes-encours-creance">
                  {money(data.voitures_impayees.reduce((s, v) => s + Number(v.montant_restant || 0), 0))}
                </span>
              </p>
            )}
          </div>
          <Link to="/ventes" className="text-sm text-klein hover:underline" data-testid="dash-see-ventes">
            Voir toutes les ventes →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">N° VO</th>
                <th className="px-6 py-3">Plaque</th>
                <th className="px-6 py-3">Véhicule</th>
                <th className="px-6 py-3">Client</th>
                <th className="px-6 py-3">Départ</th>
                <th className="px-6 py-3 text-right">Restant</th>
                <th className="px-6 py-3 text-center">Statut</th>
              </tr>
            </thead>
            <tbody>
              {data.voitures_impayees.length === 0 && (
                <tr><td colSpan={8} className="px-6 py-10 text-center text-muted-foreground">Aucune vente en cours.</td></tr>
              )}
              {data.voitures_impayees.slice(0, 8).map((v) => (
                <tr key={v.id} className="border-t hover:bg-slate-50">
                  <td className="px-6 py-3 whitespace-nowrap">{dateFR(v.date_vente)}</td>
                  <td className="px-6 py-3 font-mono text-xs text-muted-foreground">{v.numero_vo || "—"}</td>
                  <td className="px-6 py-3"><Link to={`/ventes/${v.id}`}><Plate value={v.plaque} small /></Link></td>
                  <td className="px-6 py-3"><Link to={`/ventes/${v.id}`} className="hover:underline">{v.marque} {v.modele}</Link></td>
                  <td className="px-6 py-3">{v.client_nom}</td>
                  <td className="px-6 py-3 text-muted-foreground">{v.date_depart ? dateFR(v.date_depart) : "—"}</td>
                  <td className="px-6 py-3 text-right font-mono font-semibold text-destructive">{money(v.montant_restant)}</td>
                  <td className="px-6 py-3 text-center"><StatusBadge statut={v.statut} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Snoozed alerts dialog */}
      {showSnoozedDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowSnoozedDialog(false)}
          data-testid="snoozed-alerts-dialog"
        >
          <div
            className="bg-card border border-border rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">
                  Gestion des alertes
                </div>
                <h3 className="font-display font-bold text-xl mt-1">Alertes masquées</h3>
              </div>
              <button
                onClick={() => setShowSnoozedDialog(false)}
                className="p-1.5 hover:bg-slate-100 rounded-sm"
                data-testid="snoozed-alerts-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              {snoozedAlerts.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Aucune alerte masquée.
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {snoozedAlerts.map((s, i) => {
                    const labels = {
                      retard_paiement: "Retard paiement",
                      carte_grise: "Carte grise",
                      depart_proche: "Départ proche",
                      a_preparer: "À préparer",
                    };
                    const vente = ventes.find((v) => v.id === s.vente_id);
                    return (
                      <li key={i} className="py-3 flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] tracking-wider uppercase text-muted-foreground font-semibold">
                            {labels[s.type] || s.type}
                          </div>
                          <div className="text-sm font-medium truncate">
                            {vente ? `${vente.plaque} · ${vente.marque} ${vente.modele} · ${vente.client_nom}` : `Vente ${s.vente_id}`}
                          </div>
                        </div>
                        <button
                          onClick={() => unsnoozeAlert(s.type, s.vente_id)}
                          data-testid={`unsnooze-${s.type}-${s.vente_id}`}
                          className="text-xs font-semibold uppercase tracking-wider text-klein hover:bg-klein/10 px-3 py-1.5 rounded-sm border border-klein/30"
                        >
                          Réactiver
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const COMMERCIAL_STEPS = [
  { n: 1, title: "Réception client", desc: "Créer le client (Nom, Téléphone).", to: "/clients" },
  { n: 2, title: "Créer la vente", desc: "Plaque, marque/modèle, prix, date départ prévu.", to: "/ventes" },
  { n: 3, title: "Acompte", desc: "Lier le paiement à la vente. Reçu PDF auto.", to: "/paiements" },
  { n: 4, title: "Préparation", desc: "Suivre l'atelier · cocher \"Prêt\".", to: "/atelier" },
  { n: 5, title: "Solde + Reçu", desc: "Encaisser le restant · reçu final.", to: "/paiements" },
  { n: 6, title: "Départ + Carte grise", desc: "Date départ effective · marquer CG OK.", to: "/ventes" },
  { n: 7, title: "Clôture", desc: "Le véhicule passe en VO vendus automatiquement.", to: "/vo-vendus" },
];

function CommercialChecklist() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem("ac_checklist_open") !== "0"; } catch { return true; }
  });
  const toggle = () => {
    setOpen((o) => {
      try { localStorage.setItem("ac_checklist_open", o ? "0" : "1"); } catch { /* ignore */ }
      return !o;
    });
  };
  return (
    <section className="bg-card border border-border rounded-sm" data-testid="commercial-checklist">
      <button
        type="button"
        onClick={toggle}
        data-testid="commercial-checklist-toggle"
        className="w-full flex items-center justify-between gap-4 px-5 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/40"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-sm bg-klein/10 text-klein flex items-center justify-center">
            <BookMarked size={16} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">Procédure type</div>
            <div className="font-display font-bold text-base truncate">Ce qu&apos;il faut faire — 7 étapes</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <Link
            to="/aide-commercial"
            onClick={(e) => e.stopPropagation()}
            className="hidden sm:inline text-klein hover:underline"
            data-testid="commercial-checklist-link"
          >
            Voir le guide complet →
          </Link>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>
      {open && (
        <ol className="px-5 pb-5 pt-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2">
          {COMMERCIAL_STEPS.map((s) => (
            <li key={s.n}>
              <Link
                to={s.to}
                data-testid={`commercial-checklist-step-${s.n}`}
                className="block h-full p-3 rounded-sm border border-border bg-background hover:border-klein hover:bg-klein/5 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-5 h-5 rounded-full bg-klein text-white text-[10px] font-black flex items-center justify-center">
                    {s.n}
                  </div>
                  <div className="text-xs font-bold uppercase tracking-wide">{s.title}</div>
                </div>
                <div className="text-[11px] text-muted-foreground leading-snug">{s.desc}</div>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function PendingTasksCard() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/taches", { params: { done: false } });
      setTasks(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (id) => {
    await api.put(`/taches/${id}`, { done: true });
    load();
  };

  if (loading) return null;
  if (!tasks.length) return null;

  const top = tasks.slice(0, 5);
  const rest = tasks.length - top.length;

  return (
    <section className="bg-amber-50 border border-amber-200 rounded-sm p-5" data-testid="pending-tasks-card">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-sm bg-amber-500 text-white flex items-center justify-center">
          <PackageCheck size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-amber-700 font-semibold">
                À FAIRE · TÂCHES INTERNES
              </div>
              <h3 className="font-display font-bold text-base">{tasks.length} tâche{tasks.length > 1 ? "s" : ""} en attente</h3>
            </div>
            <Link
              to="/taches"
              data-testid="pending-tasks-link"
              className="text-xs text-klein hover:underline"
            >
              Voir toutes les tâches →
            </Link>
          </div>
          <ul className="mt-3 divide-y divide-amber-200">
            {top.map((t) => (
              <li key={t.id} className="py-2 flex items-start gap-3" data-testid={`pending-task-${t.id}`}>
                <input
                  type="checkbox"
                  onChange={() => toggle(t.id)}
                  data-testid={`pending-task-check-${t.id}`}
                  className="mt-1 h-4 w-4 accent-klein cursor-pointer"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{t.text}</div>
                  <div className="flex items-center gap-3 text-[11px] text-amber-900/70 mt-0.5">
                    {t.due_date && <span>📅 {t.due_date.split("-").reverse().join("/")}</span>}
                    {t.vente_id && (
                      <Link to={`/ventes/${t.vente_id}`} className="text-klein hover:underline">
                        Vente liée →
                      </Link>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {rest > 0 && (
            <div className="text-xs text-amber-700 mt-2">
              + {rest} autre{rest > 1 ? "s" : ""} tâche{rest > 1 ? "s" : ""}…
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

